import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'ngx-webstorage';
import { PrimeNG } from 'primeng/config';
import { UI_LANGUAGE_CODE } from '../../constants/general-constants';
import { getAppConfig } from '../../../app.config';
import { getDefaultUiLanguage } from '../../../common/utilities';
import { readPersistedUiState } from '../../../data/client-state/ui-state-persistence';
import { LanguageCode } from '../../../generated/generated-types';
import { I18nService } from './i18n.service';
import { extractPrimeNgTranslation } from './primeng-translation.util';

export function initUiLanguagesInitializer(
    primeNG: PrimeNG,
    translateService: TranslateService,
    i18nService: I18nService,
    localStorageService: LocalStorageService,
): () => Promise<void> {
    return () => {
        const defaultLanguage = getDefaultUiLanguage();
        const persisted = readPersistedUiState(localStorageService);
        const availableLanguages = getAppConfig().availableLanguages ?? [LanguageCode.de];

        if (!availableLanguages.includes(defaultLanguage)) {
            throw new Error(
                `The defaultLanguage "${defaultLanguage}" must be one of the availableLanguages [${availableLanguages
                    .map(l => `"${l}"`)
                    .join(', ')}]`,
            );
        }
        const uiLanguage = availableLanguages.includes(persisted.language)
            ? persisted.language
            : defaultLanguage;
        localStorageService.store(UI_LANGUAGE_CODE, uiLanguage);
        i18nService.setLanguage(uiLanguage);
        i18nService.setDefaultLanguage(defaultLanguage);
        i18nService.setAvailableLanguages(availableLanguages || [defaultLanguage]);
        setPrimeNgtDefaultLocaleTranslation(primeNG, translateService, i18nService);
        registerLocaleData(localeDe);
        return Promise.resolve(void 0);
    };
}

const setPrimeNgtDefaultLocaleTranslation = (
    primeNG: PrimeNG,
    translateService: TranslateService,
    i18nService: I18nService,
) => {
    const lang = i18nService.getCurrentLanguage();
    translateService.use(lang).subscribe({
        next: (allTranslations) => {
            const primeng = extractPrimeNgTranslation(allTranslations);
            if (primeng) {
                primeNG.setTranslation(primeng);
            } else {
                console.error('PrimeNG translations not found for language:', lang);
            }
        },
        error: (err) => {
            console.error('Error loading translations for language:', lang, err);
        },
    });
};
